import { Link } from "react-router-dom";
import { getSoorten, soortSlug, type Soort } from "@/lib/catalog";
import { useSoorten } from "@/lib/catalog-view";
import { useIsoMargins, useBengMargins } from "@/lib/settings";
import { isoMinVanafCents } from "@/features/isolatieglas/isolatieglas-data";
import { bengMinVanafCents } from "@/features/benglas/benglas-data";
import { formatEuro } from "@/lib/format";
import { useT } from "@/lib/i18n";
import { btwAmount, btwLabel, useBtwPreference } from "@/lib/btw";
import { cdnImage, IMG } from "@/lib/img";
import { glassPhotoTile } from "@/lib/glass-photos";

export interface GlassCat {
  name: string;
  intro: string;
  to: string;
  img?: string;
  vanafCents?: number;
}

/** Subcategorieën die samen de categorie "Veiligheidsglas" vormen (zie CATEGORY_META). */
const VEILIGHEID_SUBS = new Set(["Gehard glas", "Gelaagd glas", "Gehard-gelaagd glas", "Beloopbaar glas"]);

/** Laagste vanaf-prijs voor isolatie-/vacuümglas — komen niet uit `soorten` maar uit hun eigen
 *  prijsmodules (met admin-marges). Geef mee via `useGlassCats`. */
export interface ModuleVanaf {
  iso?: number | null;
  vacuum?: number | null;
}

/** Alle glascategorieën met foto (waar beschikbaar) + laagste vanaf-prijs uit de bijbehorende
 *  landingspagina. Enkel/veiligheid/installatie/spiegels komen uit `soorten`; isolatie- en
 *  vacuümglas via `moduleVanaf` (aparte prijsmodules). Geef `soorten` mee (via useGlassCats) om
 *  de admin-overrides in de vanaf-prijs te krijgen. */
export function glassCats(soorten: Soort[] = getSoorten(), moduleVanaf: ModuleVanaf = {}): GlassCat[] {
  const bySlug = (slug: string) => soorten.find((s) => soortSlug(s) === slug);
  const minOf = (pred: (s: Soort) => boolean): number | undefined => {
    const prices = soorten.filter(pred).map((s) => s.vanafCents).filter((v) => v > 0);
    return prices.length ? Math.min(...prices) : undefined;
  };
  const spiegels = bySlug("spiegels-op-maat");
  // Vaste volgorde: enkel → isolatie → vacuüm → installatie → spiegels → veiligheidsglas.
  // (Figuurglas valt onder Enkel glas — geen aparte tegel.)
  return [
    { name: "Enkel glas", intro: "Float-, mat- en figuurglas op maat.", to: "/glas-op-maat", img: bySlug("floatglas")?.img, vanafCents: minOf((s) => s.productSlug === "enkel-glas") },
    { name: "Isolatieglas", intro: "Dubbel & triple — HR++/HR+++.", to: "/categorie/isolatieglas", img: "/glas-foto/isolatieglas-dubbel.webp", vanafCents: moduleVanaf.iso ?? undefined },
    { name: "Vacuümglas", intro: "Ultradun met topisolatie.", to: "/vacuumglas", img: "/glas-foto/vacuumglas-tegel.webp", vanafCents: moduleVanaf.vacuum ?? undefined },
    { name: "Installatieglas", intro: "Glazen deuren op maat, met beslag-uitsparingen.", to: "/categorie/installatieglas", img: "/glas-foto/gelaagd.webp", vanafCents: minOf((s) => s.productSlug === "installatieglas") },
    { name: "Spiegels", intro: "Spiegels op maat, met boring of facet.", to: "/glas-op-maat/spiegels-op-maat", img: spiegels?.img, vanafCents: spiegels?.vanafCents },
    { name: "Veiligheidsglas", intro: "Gehard, gelaagd, gehard-gelaagd & beloopbaar.", to: "/categorie/veiligheidsglas", img: "/glas-foto/gelaagd.webp", vanafCents: minOf((s) => VEILIGHEID_SUBS.has(s.subcategory)) },
  ];
}

/** Pak categorieën op naam in een vaste volgorde (voor een curated subset). */
export function pickGlassCats(names: string[], soorten?: Soort[], moduleVanaf: ModuleVanaf = {}): GlassCat[] {
  const all = glassCats(soorten, moduleVanaf);
  return names.map((n) => all.find((c) => c.name === n)).filter((c): c is GlassCat => !!c);
}

/** Reactieve varianten die de live admin-overrides/-marges toepassen op de vanaf-prijzen. */
export function useGlassCats(): GlassCat[] {
  return glassCats(useSoorten(), useModuleVanaf());
}
export function usePickGlassCats(names: string[]): GlassCat[] {
  return pickGlassCats(names, useSoorten(), useModuleVanaf());
}

/** Laagste isolatie-/vacuümglas-prijs met de live admin-marges. */
function useModuleVanaf(): ModuleVanaf {
  return { iso: isoMinVanafCents(useIsoMargins()), vacuum: bengMinVanafCents(useBengMargins()) };
}

const PaneIcon = () => (
  <svg viewBox="0 0 24 24" width="44" height="44" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <rect x="3" y="3" width="18" height="18" rx="1.5" />
    <path d="M3 12h18M12 3v18" />
  </svg>
);

/** Grid van glascategorie-tegels (foto of nette merk-tegel + vanaf-prijs). */
export function GlassGrid({ cats, cols = 4 }: { cats: GlassCat[]; cols?: 3 | 4 }) {
  const t = useT();
  const [pref] = useBtwPreference();
  return (
    <div className={`grid grid-cols-2 gap-2.5 sm:gap-4 ${cols === 4 ? "lg:grid-cols-4" : "lg:grid-cols-3"}`}>
      {cats.map((c) => (
        <Link
          key={c.name}
          to={c.to}
          className="group flex flex-col rounded-2xl border border-primary-100 bg-white p-3 shadow-sm transition hover:-translate-y-0.5 hover:border-primary-300 hover:shadow-md sm:p-4"
        >
          <div className="relative mb-3 aspect-[4/3] w-full overflow-hidden rounded-xl bg-primary-50 sm:mb-4">
            {c.img ? (
              <img src={cdnImage(glassPhotoTile(c.img), { w: IMG.card })} alt={t(c.name)} loading="lazy" decoding="async" className="absolute inset-0 h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
            ) : (
              <div className="absolute inset-0 flex items-center justify-center text-primary-300">
                <PaneIcon />
              </div>
            )}
          </div>
          <h3 className="text-sm font-bold text-primary-900 sm:text-lg">{t(c.name)}</h3>
          <p className="mt-0.5 line-clamp-1 hidden text-sm text-primary-600 sm:block">{t(c.intro)}</p>
          <div className="mt-2.5 flex items-end justify-between border-t border-primary-50 pt-2 sm:mt-4 sm:pt-3">
            {c.vanafCents != null ? (
              <div className="flex flex-col">
                <span className="text-[10px] font-medium uppercase tracking-wider text-primary-600 sm:text-[11px]">{t("Vanaf")}</span>
                <span className="text-sm font-bold text-accent-700 sm:text-lg">
                  {formatEuro(btwAmount(c.vanafCents, pref))} <span className="text-xs font-normal text-primary-600">/ m²<span className="hidden sm:inline"> {t(btwLabel(pref))}</span></span>
                </span>
              </div>
            ) : (
              <span className="text-xs font-medium text-primary-600 sm:text-sm">{t("Op maat")}</span>
            )}
            <span className="flex h-7 w-7 items-center justify-center rounded-lg bg-accent-700 text-sm text-white shadow-sm transition group-hover:bg-accent-800 group-hover:shadow-md sm:h-10 sm:w-10 sm:text-base">→</span>
          </div>
        </Link>
      ))}
    </div>
  );
}
